// Message priority, the Mac's three pieces: the "!" toolbar menu (MacComposerControls' inline
// Estándar/Importante/Urgente picker, tinted by the current choice), the "Se enviará como …" line
// above the field (MacConversationView.composerFieldIdle) and the pill on a post
// (MessagePriorityPill: its own line, on every post that carries one).
import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { MessagePriority, type Level } from '../../../core/messagePriority';
import { Icon } from '../../../design/icons';
import { t } from '../../../i18n/i18n';
import { AckMenuItems } from '../threads/AckComposer';
import type { AckChoice } from '../../../core/postAcknowledgements';
import './compose.css';

/** §41.3 rows under the levels (features/threads): shown only when the server allows them. */
export interface AckMenu {
  canRequest: boolean;
  canPersist: boolean;
  value: AckChoice;
  onChange(v: AckChoice): void;
}

export function PriorityButton({ value, onChange, ack }: { value: Level | null; onChange(v: Level | null): void; ack?: AckMenu }) {
  const button = useRef<HTMLButtonElement>(null);
  const [open, setOpen] = useState(false);
  const label = t('channel.priority.menu');
  return (
    <>
      <button
        ref={button}
        type="button"
        className={`icon-button compose-priority${value ? ' ' + value : ''}${ack?.value.requestAck ? ' acks' : ''}`}
        title={label}
        aria-label={label}
        aria-haspopup="menu"
        aria-expanded={open}
        onMouseDown={(e) => e.preventDefault()}
        onClick={() => setOpen((o) => !o)}
      >
        <Icon name="alertCircle" size={17} strokeWidth={value ? 2 : 1.6} />
      </button>
      {open && button.current ? (
        <PriorityMenu
          anchor={button.current}
          value={value}
          ack={ack}
          onClose={() => setOpen(false)}
          onPick={(v) => {
            setOpen(false);
            onChange(v);
          }}
        />
      ) : null}
    </>
  );
}

function PriorityMenu({ anchor, value, ack, onClose, onPick }: { anchor: HTMLElement; value: Level | null; ack?: AckMenu; onClose(): void; onPick(v: Level | null): void }) {
  const ref = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState<{ left: number; top: number } | null>(null);
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const a = anchor.getBoundingClientRect();
    const r = el.getBoundingClientRect();
    // Opens UP from the toolbar (the box sits at the bottom of the window).
    setPos({ left: Math.max(8, Math.min(a.left, window.innerWidth - r.width - 8)), top: Math.max(8, a.top - r.height - 6) });
  }, [anchor]);
  useEffect(() => {
    const close = (e: MouseEvent) => !ref.current?.contains(e.target as Node) && !anchor.contains(e.target as Node) && onClose();
    const esc = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      e.stopPropagation();
      onClose();
    };
    window.addEventListener('mousedown', close);
    window.addEventListener('keydown', esc, true);
    window.addEventListener('blur', onClose);
    return () => {
      window.removeEventListener('mousedown', close);
      window.removeEventListener('keydown', esc, true);
      window.removeEventListener('blur', onClose);
    };
  }, [anchor, onClose]);
  useEffect(() => {
    ref.current?.querySelector<HTMLElement>('[aria-checked="true"]')?.focus();
  }, []);
  return createPortal(
    <div
      ref={ref}
      className="popover compose-priority-menu"
      role="menu"
      style={pos ? { left: pos.left, top: pos.top } : { left: -9999, top: -9999 }}
      onKeyDown={(e) => {
        if (e.key !== 'ArrowDown' && e.key !== 'ArrowUp') return;
        e.preventDefault();
        const items = [...(ref.current?.querySelectorAll<HTMLElement>('.menu-item') ?? [])];
        const i = items.indexOf(document.activeElement as HTMLElement);
        items[(i + (e.key === 'ArrowDown' ? 1 : items.length - 1)) % items.length]?.focus();
      }}
    >
      <div className="menu-title">{t('channel.priority.menu')}</div>
      {MessagePriority.choices.map((choice) => (
        <button
          key={choice ?? 'standard'}
          type="button"
          className="menu-item"
          role="menuitemradio"
          aria-checked={choice === value}
          onClick={() => onPick(choice)}
        >
          <span className={`compose-dot${choice ? ' ' + choice : ''}`} />
          {t(MessagePriority.choiceKey(choice))}
          {choice === value ? <Icon name="check" size={15} className="compose-check" /> : null}
        </button>
      ))}
      {ack?.canRequest ? <AckMenuItems ack={ack} /> : null}
    </div>,
    document.body,
  );
}

/** "Se enviará como importante" — while a priority is set; the × is the web's quick way back. */
export function PriorityLine({ value, onClear }: { value: Level; onClear(): void }) {
  return (
    <div className={`compose-will-send ${value}`} role="status">
      <Icon name="alertCircle" size={13} strokeWidth={2} />
      {t(MessagePriority.willSendKey(value))}
      <button type="button" title={t('web.compose.priority.clear')} aria-label={t('web.compose.priority.clear')} onMouseDown={(e) => e.preventDefault()} onClick={onClear}>
        <Icon name="x" size={12} strokeWidth={2.2} />
      </button>
    </div>
  );
}

/** The sender's priority on a post; standard draws nothing. */
export function PriorityPill({ priority }: { priority: string }) {
  const key = MessagePriority.pillKey(priority);
  if (!key) return null;
  return <span className={`compose-pill ${MessagePriority.level(priority)}`}>{t(key)}</span>;
}
